import React from 'react';
import {
  Flex, Text, Icon, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow,
  useMediaQuery,
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/intlFormatters';

// A tone's colour: a warning's amber text, or the problem red.
const TONE_COLOR = { orange: 'redesign.text.warn', red: 'redesign.accent.red' };

/**
 * MI-298: discloses the movements/balances left out of a converted total, and
 * lets the user find out *why*.
 *
 * The count alone ("1323 movements not converted") reads as a defect nobody can
 * act on; the cause is what makes it actionable, and in the common case (a
 * custom currency with no manual rate) the fix is thirty seconds away.
 *
 * The reason sits behind an explicit info affordance rather than inline (two
 * fixed lines outweighed the figure they annotate) and rather than a bare hover
 * tooltip (undiscoverable — the version that shipped first failed exactly that
 * way). The icon is what announces there is more to see; that, not the trigger,
 * is what fixes discoverability.
 *
 * How it opens follows the *pointer*, not the screen size: `(hover: hover) and
 * (pointer: fine)` asks whether this input can actually rest on a target, which
 * is true for a mouse, false for a finger, and re-evaluated on hybrids as the
 * user switches between them — a width breakpoint would get touch laptops and
 * narrow desktop windows wrong. Keyboard users are covered either way, since
 * Chakra opens on focus too and the trigger is a real <button>.
 */
export default function UnconvertibleNote({
  buckets = [],
  count,
  variant = 'movements',
  fontSize = 'xs',
  justify = 'flex-start',
  // MI-298: the single date a balance is valued at (YYYY-MM-DD). Balances convert
  // at today's rate, so when a standard currency has no rate we can name the exact
  // day that's missing — unlike movements, which each convert at their own date.
  date = null,
  // The headline and icon's tone (see TONE_COLOR). Orange (informational) by
  // default; Accounts uses red to match its boxed treatment.
  tone = 'orange',
}) {
  const { t } = useTranslation('common');
  // False on touch, and false before the query resolves — so the click
  // trigger, which works everywhere, is what we fall back to.
  const [canHover] = useMediaQuery('(hover: hover) and (pointer: fine)');

  const total = count ?? buckets.reduce((s, b) => s + (b.count || 0), 0);
  if (!total) return null;

  const single = buckets.length === 1 ? buckets[0] : null;
  const isBalances = variant === 'balances';

  const suffix = (base) => `convert.${isBalances ? base.replace('notConverted', 'notConvertedBalances') : base}`;
  const headline = single?.currency
    ? t(suffix(total === 1 ? 'notConvertedCurrencyOne' : 'notConvertedCurrencyOther'),
        { count: total, currency: single.currency })
    : t(suffix(total === 1 ? 'notConvertedOne' : 'notConvertedOther'), { count: total });

  // With several currencies at fault the causes may differ, so name them
  // instead of asserting one reason that would be wrong for the rest.
  const reasonCode = single?.reason || 'no_market_rate';
  // A standard currency with no rate, valued at a known day, names that day.
  // Custom currencies (a config gap, date-independent) keep their plain message.
  const reasonKey = date && reasonCode === 'no_market_rate'
    ? 'convert.reason.no_market_rate_dated'
    : `convert.reason.${reasonCode}`;
  const reason = single
    ? t(reasonKey, {
        currency: single.currency,
        date: date ? formatDate(date) : undefined,
        defaultValue: t('convert.reason.no_market_rate', { currency: single.currency }),
      })
    : t('convert.reasonMultiple', {
        currencies: buckets.map((b) => b.currency).filter(Boolean).join(', '),
      });

  return (
    <Flex align="center" gap={1} justify={justify}>
      <Text fontSize={fontSize} color={TONE_COLOR[tone]}>{headline}</Text>
      <Popover placement="top" trigger={canHover ? 'hover' : 'click'} isLazy>
        <PopoverTrigger>
          <Flex
            as="button"
            type="button"
            align="center"
            aria-label={t('convert.whyNotConverted')}
            color={TONE_COLOR[tone]}
            _hover={{ color: 'redesign.heading' }}
          >
            <Icon as={FiInfo} boxSize={3.5} />
          </Flex>
        </PopoverTrigger>
        <PopoverContent w="260px" borderRadius="lg" _focus={{ outline: 'none' }}>
          <PopoverArrow />
          <PopoverBody fontSize="xs" color="redesign.text">{reason}</PopoverBody>
        </PopoverContent>
      </Popover>
    </Flex>
  );
}
